import { useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Link } from "wouter";
import { cn } from "./cn";
import { Mark } from "./Mark";

export type ToastTone = "ok" | "bad" | "plain";
/** A link at the end of a toast, to where the user can act on it. A `#hash` in `href` scrolls there. */
export type ToastLink = { href: string; label: string };
type Item = { id: number; text: string; tone: ToastTone; link?: ToastLink };

let items: Item[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

function dismiss(id: number) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  items = items.filter((t) => t.id !== id);
  emit();
}

function toast(text: string, tone: ToastTone = "plain", link?: ToastLink) {
  const id = ++seq;
  items = [...items, { id, text, tone, link }];
  while (items.length > 3) {
    const oldest = items[0]!;
    clearTimeout(timers.get(oldest.id));
    timers.delete(oldest.id);
    items = items.slice(1);
  }
  timers.set(id, setTimeout(() => dismiss(id), link ? 8000 : 3500));
  emit();
  return id;
}

const api = { toast, dismiss };

export function useToast() {
  return api;
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function ToastIcon({ tone }: { tone: ToastTone }) {
  return (
    <motion.span
      aria-hidden
      className={cn(
        "grid size-6 shrink-0 place-items-center rounded-chip",
        tone === "ok" ? "bg-ok text-white" : tone === "bad" ? "bg-bad text-white" : "bg-white",
      )}
      initial={{ scale: 0.3, opacity: 0, filter: "blur(4px)" }}
      animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
      transition={{ type: "spring", stiffness: 380, damping: 14, delay: 0.08 }}
    >
      {tone === "ok" ? (
        <svg viewBox="0 0 16 16" className="size-4" fill="none">
          <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : tone === "bad" ? (
        <svg viewBox="0 0 16 16" className="size-4" fill="none">
          <path d="M8 4v5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="8" cy="12" r="1.2" fill="currentColor" />
        </svg>
      ) : (
        <Mark size={16} />
      )}
    </motion.span>
  );
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, () => items, () => items);
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4",
        "bottom-[calc(env(safe-area-inset-bottom)+5.75rem)] md:inset-x-auto md:right-6 md:bottom-6 md:items-end md:px-0",
      )}
    >
      <AnimatePresence initial={false}>
        {list.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.92, filter: "blur(2px)" }}
            transition={{ type: "spring", stiffness: 420, damping: 28 }}
            className={cn(
              "pointer-events-auto flex min-h-11 max-w-[min(440px,100%)] cursor-pointer items-center gap-3 rounded-lg bg-accent py-2 pl-2.5 text-sm font-semibold text-on-accent shadow-raised",
              t.link ? "pr-1.5" : "pr-4",
            )}
            onClick={() => dismiss(t.id)}
          >
            <ToastIcon tone={t.tone} />
            <span className="text-pretty">{t.text}</span>
            {t.link && (
              <Link
                href={t.link.href}
                // wouter moves with pushState, which fires no hashchange, so the page hears about the hash here.
                onClick={() => setTimeout(() => dispatchEvent(new HashChangeEvent("hashchange")))}
                className="-my-0.5 ml-auto shrink-0 whitespace-nowrap rounded-control px-3 py-2 text-white transition-[background-color] duration-150 hover:bg-white/15"
              >
                {t.link.label}
              </Link>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
